import React, { useState } from "react";
import { Pagination, Tabs } from "antd";
import { useInfluencerPaymentHistory } from "../../hooks/usePayment";
import { formatCurrency, parseApiAmount } from "../../utils/amountFormat";
import {
  influencerPaymentStatusClass,
  influencerPaymentStatusLabel,
} from "../../utils/influencerPaymentStatus";

// 크리에이터는 null, pending, paid 상태만 보여줌
const TABS = [
  { key: "all", label: "전체" },
  { key: "pending", label: "지급대기" },
  { key: "paid", label: "지급완료" },
];

export const InfluencerPaymentHistory = () => {
  const [activeTab, setActiveTab] = useState("all");
  const status = activeTab === "all" ? null : activeTab;
  const [page, setPage] = useState(1);

  const { data, isLoading } = useInfluencerPaymentHistory({
    campaignId: null,
    status,
  });

  const total = data?.total;
  const list = Array.isArray(data?.result) ? data?.result : [];

  const tabItems = TABS.map((tab) => ({
    key: tab.key,
    label: tab.label,
  }));

  return (
    <div className="w-full max-w-[1400px]">
      <h2 className="text-3xl font-bold text-[#111111] md:text-4xl">
        정산 내역
      </h2>

      <Tabs
        activeKey={activeTab}
        items={tabItems}
        onChange={setActiveTab}
        className="w-full mb-5 font-normal"
      />

      {list?.length === 0 && !isLoading && (
        <div className="rounded-[22px] border border-dashed border-[#e7eaee] bg-[#fafbfc] px-6 py-12 text-center text-[15px] text-[#7a7f87]">
          정산 내역이 없습니다.
        </div>
      )}

      <div className="space-y-4">
        {list?.map((item) => {
          const net = parseApiAmount(item.net_amount);
          const currency = item.currency ?? "KRW";
          const title = item.campaign_title ?? "캠페인";
          const thumb = item.campaign_thumbnail;
          const range =
            item.campaign_start_at && item.campaign_end_at
              ? `${item.campaign_start_at.split(" ")[0]} ~ ${
                  item.campaign_end_at.split(" ")[0]
                }`
              : null;

          return (
            <article
              key={item.id}
              className="overflow-hidden rounded-[22px] border border-[#e7eaee] bg-white hover:-translate-y-0.5 hover:border-[#cfd7e2] hover:shadow-[0_18px_44px_rgba(17,24,39,0.08)]"
            >
              <div className="flex flex-col gap-4 p-5 md:flex-row md:items-stretch md:gap-5 md:px-6 md:py-4">
                <div className="shrink-0">
                  {thumb ? (
                    <div className="h-[100px] w-full overflow-hidden rounded-2xl bg-[#f1f5f9] sm:h-[120px] sm:w-[160px]">
                      <img
                        src={thumb}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="flex h-[100px] w-full items-center justify-center rounded-2xl bg-gradient-to-br from-[#e8eef5] to-[#f1f5f9] sm:h-[120px] sm:w-[160px]">
                      <span className="text-[12px] font-medium text-[#94a3b8]">
                        썸네일 없음
                      </span>
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <span
                    className={`inline-flex rounded-[8px] px-2.5 py-1 text-[12px] font-bold ${influencerPaymentStatusClass(
                      item.status
                    )}`}
                  >
                    {influencerPaymentStatusLabel(item.status)}
                  </span>

                  <h3 className="mt-2.5 break-keep text-[17px] font-semibold leading-[1.4] tracking-[-0.03em] text-[#3f4650] md:text-[19px]">
                    {title}
                  </h3>

                  {range ? (
                    <p className="mt-1.5 text-[13px] text-[#7a7f87]">{range}</p>
                  ) : null}

                  <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-[#8a929d]">
                    {item.paid_at && <span>지급일 {item.paid_at}</span>}
                  </div>
                </div>

                <div className="flex shrink-0 flex-col justify-center border-t border-[#edf0f3] pt-4 md:w-[180px] md:border-l md:border-t-0 md:pl-5 md:pt-0">
                  <span className="text-[12px] font-medium text-[#9aa1aa]">
                    실지급액
                  </span>
                  <span className="text-[20px] font-bold tracking-[-0.04em] text-[#111827]">
                    {formatCurrency(net, currency)}
                  </span>
                  <span className="mt-1 text-[11px] text-[#b0b8c1]">
                    리워드{" "}
                    {formatCurrency(
                      parseApiAmount(item.settlement_amount),
                      currency
                    )}
                  </span>
                  <span className="mt-1 text-[11px] text-[#b0b8c1]">
                    원천징수{" "}
                    {formatCurrency(parseApiAmount(item.tax_amount), currency)}
                  </span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
      <Pagination
        current={page}
        total={total}
        pageSize={20}
        onChange={(page) => setPage(page)}
        showSizeChanger={false}
        className="mt-12 justify-center"
      />
    </div>
  );
};
